import React, { useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { KeyRound, User as UserIcon, Eye, EyeOff } from "lucide-react";

export default function Profilo() {
  const { user } = useAuth();
  const [form, setForm] = useState({ current_password: "", new_password: "", confirm_password: "" });
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.current_password || !form.new_password) {
      toast.error("Compila tutti i campi");
      return;
    }
    if (form.new_password.length < 6) {
      toast.error("La nuova password deve avere almeno 6 caratteri");
      return;
    }
    if (form.new_password !== form.confirm_password) {
      toast.error("La conferma password non corrisponde");
      return;
    }
    if (form.current_password === form.new_password) {
      toast.error("La nuova password deve essere diversa da quella attuale");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/auth/change-password", {
        current_password: form.current_password,
        new_password: form.new_password,
      });
      toast.success("Password aggiornata con successo");
      setForm({ current_password: "", new_password: "", confirm_password: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6 max-w-3xl" data-testid="profilo-page">
      <div>
        <div className="label-tag mb-2">Account</div>
        <h1 className="font-brand text-4xl sm:text-5xl font-light tracking-tight">Il tuo profilo</h1>
        <p className="text-sm mt-2" style={{ color: "var(--ds-text-secondary)" }}>
          Gestisci le informazioni del tuo account e la password di accesso.
        </p>
      </div>

      {/* Info account */}
      <div className="ds-card p-6">
        <div className="flex items-center gap-3 mb-4">
          <div
            className="w-10 h-10 rounded-full flex items-center justify-center"
            style={{ background: "var(--ds-primary-soft)" }}
          >
            <UserIcon size={18} style={{ color: "var(--ds-primary)" }} />
          </div>
          <div>
            <div className="font-medium">{user?.nome || user?.email}</div>
            <div className="text-xs" style={{ color: "var(--ds-text-secondary)" }}>{user?.email}</div>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
          <div>
            <div className="label-tag">Ruolo</div>
            <div className="mt-1">{user?.role === "admin" ? "Amministratore" : user?.ruolo_nome || "Utente"}</div>
          </div>
          <div>
            <div className="label-tag">Nome</div>
            <div className="mt-1">{user?.nome || "—"}</div>
          </div>
          <div>
            <div className="label-tag">Email</div>
            <div className="mt-1">{user?.email}</div>
          </div>
        </div>
      </div>

      {/* Cambio password */}
      <form onSubmit={submit} className="ds-card p-6 space-y-5">
        <div className="flex items-center gap-3">
          <KeyRound size={18} style={{ color: "var(--ds-primary)" }} />
          <div>
            <div className="font-medium">Cambia password</div>
            <div className="text-xs" style={{ color: "var(--ds-text-secondary)" }}>
              Almeno 6 caratteri. Scegli una password che non usi altrove.
            </div>
          </div>
        </div>

        <div>
          <Label className="label-tag" htmlFor="current-password">Password attuale</Label>
          <div className="relative mt-1.5">
            <Input
              id="current-password"
              type={showCurrent ? "text" : "password"}
              value={form.current_password}
              onChange={(e) => setForm({ ...form, current_password: e.target.value })}
              className="pr-10 rounded-lg"
              autoComplete="current-password"
              data-testid="profilo-current-password"
              required
            />
            <button
              type="button"
              onClick={() => setShowCurrent((v) => !v)}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1"
              tabIndex={-1}
              aria-label={showCurrent ? "Nascondi" : "Mostra"}
            >
              {showCurrent ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
        </div>

        <div>
          <Label className="label-tag" htmlFor="new-password">Nuova password</Label>
          <div className="relative mt-1.5">
            <Input
              id="new-password"
              type={showNew ? "text" : "password"}
              value={form.new_password}
              onChange={(e) => setForm({ ...form, new_password: e.target.value })}
              className="pr-10 rounded-lg"
              autoComplete="new-password"
              minLength={6}
              data-testid="profilo-new-password"
              required
            />
            <button
              type="button"
              onClick={() => setShowNew((v) => !v)}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1"
              tabIndex={-1}
              aria-label={showNew ? "Nascondi" : "Mostra"}
            >
              {showNew ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
        </div>

        <div>
          <Label className="label-tag" htmlFor="confirm-password">Conferma nuova password</Label>
          <Input
            id="confirm-password"
            type={showNew ? "text" : "password"}
            value={form.confirm_password}
            onChange={(e) => setForm({ ...form, confirm_password: e.target.value })}
            className="mt-1.5 rounded-lg"
            autoComplete="new-password"
            minLength={6}
            data-testid="profilo-confirm-password"
            required
          />
        </div>

        <div className="flex justify-end">
          <Button type="submit" disabled={submitting} className="ds-btn-primary" data-testid="profilo-submit">
            {submitting ? "Salvataggio…" : "Aggiorna password"}
          </Button>
        </div>
      </form>
    </div>
  );
}
